import { useState } from 'react';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '../ui/sheet';
import { Menu, Phone, MessageCircle, CalendarCheck } from 'lucide-react';
import { useLang } from '../../i18n/LanguageContext';
import { BookNowButton } from './BookNowButton';
import { LanguageSwitcher } from './LanguageSwitcher';
import { LOGO, navLinks, scrollToId, CALL_TEL, whatsappUrl } from './constants';

export const Navbar = () => {
  const { t } = useLang();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const goTo = (id) => {
    scrollToId(id);
    setIsMenuOpen(false);
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-ink/85 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-[68px] flex items-center justify-between gap-4">
        <button onClick={() => goTo('home')} className="flex items-center gap-3 shrink-0" data-testid="nav-logo">
          <img src={LOGO} alt="The Real Turkish Barber Bray logo" className="h-11 w-auto" />
          <span className="hidden sm:block font-serif text-lg lg:text-xl font-bold text-gold tracking-wide leading-none">
            THE REAL TURKISH BARBER
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-7">
          {navLinks.map((l) => (
            <button
              key={l.id}
              onClick={() => goTo(l.id)}
              className="text-sm font-medium text-white/80 hover:text-gold transition-colors tracking-wide"
              data-testid={`nav-${l.id}-link`}
            >
              {t.nav[l.key]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <LanguageSwitcher className="hidden sm:inline-flex" />
          <BookNowButton
            testId="nav-book-now-button"
            className="hidden sm:inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-ink font-bold text-sm uppercase tracking-widest px-6 py-2.5 transition-colors"
          >
            <CalendarCheck size={16} />
            {t.nav.bookNow}
          </BookNowButton>

          <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden text-gold" data-testid="mobile-menu-trigger">
                <Menu size={26} />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-ink border-l border-white/10 text-white w-[290px]">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <div className="mt-6 mb-4"><LanguageSwitcher /></div>
              <nav className="flex flex-col gap-1">
                {navLinks.map((l) => (
                  <button
                    key={l.id}
                    onClick={() => goTo(l.id)}
                    className="text-left text-base font-medium py-3 border-b border-white/10 hover:text-gold transition-colors uppercase tracking-wide"
                    data-testid={`mobile-nav-${l.id}-link`}
                  >
                    {t.nav[l.key]}
                  </button>
                ))}
                <BookNowButton
                  testId="mobile-menu-book-now-button"
                  className="mt-5 inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold uppercase tracking-widest py-3.5"
                >
                  <CalendarCheck size={18} /> {t.nav.bookNow}
                </BookNowButton>
                <a href={CALL_TEL} className="mt-2 inline-flex items-center justify-center gap-2 border border-gold text-gold font-bold uppercase tracking-widest py-3.5" data-testid="mobile-menu-call-button">
                  <Phone size={18} /> {t.hero.call}
                </a>
                <a href={whatsappUrl(t.whatsappMsg)} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-bold uppercase tracking-widest py-3.5" data-testid="mobile-menu-whatsapp-button">
                  <MessageCircle size={18} /> WhatsApp
                </a>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
